import React from 'react';

export const updated = '29 September 2025';

const privacyContentEn = (
  <>
    <h2>Privacy Policy</h2>
    <p><strong>Last Updated:</strong> {updated}</p>

    <h2>1. Introduction and Scope</h2>
    <p>
      Onyx AI ("we", "our", "us") values your privacy and is committed to protecting your personal information. This
      Privacy Policy explains how we collect, use, store, and delete data when you use the Onyx AI app ("App").
    </p>

    <h2>2. User Eligibility</h2>
    <p>This App is intended for users aged <strong>18 years and older</strong>. By using Onyx AI you confirm you meet this age requirement.</p>

    <h2>3. Information We Collect</h2>
    <ul>
      <li>
        <strong>Camera &amp; Image Data:</strong> We request access to your device camera to capture photos for
        generating AI-powered content. These images are processed temporarily for AI content creation. No facial
        recognition or biometric data is collected. Images are not stored, retained, or analyzed. Once processed,
        all images are immediately deleted.
      </li>
      <li>
        <strong>User Content:</strong> Images or generated outputs you choose to save are stored locally on your
        device or in your account (if the feature is provided) and remain under your control. We do not store
        photos or any other content beyond this scope.
      </li>
      <li>
        <strong>Technical &amp; Usage Data:</strong> We may collect anonymous, aggregated technical data (device
        model, OS version, app usage patterns) solely to improve performance and security. This does not include
        personally identifiable information.
      </li>
    </ul>

    <h2>4. Third-Party Processing and Retention</h2>
    <p>
      We may use trusted third-party services to process images and provide features (for example: Replicate API for
      model inference, Google Cloud for temporary storage). All transfers use encrypted channels (HTTPS). Uploaded
      images are deleted from our systems and third-party processors within <strong>24 hours</strong> after
      processing, unless you explicitly save them.
    </p>

    <h2>5. How We Use Your Information</h2>
    <p>
      We use collected information to provide, maintain, and improve App features, for security, and to comply with
      legal obligations. We do not sell or rent personal data to third parties.
    </p>

    <h2>6. Your Rights</h2>
    <p>
      You may request deletion of your data at any time by contacting us (see contact below). We will respond and
      process deletion requests within a reasonable timeframe and may ask for identity verification to honor the
      request.
    </p>

    <h2>7. Security</h2>
    <p>
      We implement reasonable technical and organizational measures to protect data. However, no system is
      completely secure; we cannot guarantee absolute security.
    </p>

    <h2>8. Changes to this Policy</h2>
    <p>We may update this Policy from time to time; material changes will be posted on this page with an updated "Last Updated" date.</p>

    <h2>9. Contact</h2>
    <p>For questions, data deletion requests or other inquiries, contact us at <a className="l" href="mailto:onyxai.hub@gmail.com">onyxai.hub@gmail.com</a>.</p>

    <h2>10. Face Data</h2>
    <p>
      The app does not collect or store any facial data. User-uploaded images are processed temporarily to generate
      AI-powered content and deleted immediately after processing. No facial recognition or biometric data is stored
      or shared.
    </p>

    <h2>11. Privacy Policy URL</h2>
    <p>Privacy Policy URL (example): <a className="l" href="https://www.onyxup.net/terms">https://www.onyxup.net/terms</a></p>
  </>
);

const termsContentEn = (
  <>
    <h2>Terms of Use (EULA)</h2>
    <p><strong>Last Updated:</strong> {updated}</p>

    <h2>1. Agreement</h2>
    <p>By downloading or using Onyx AI ("App"), you agree to these Terms of Use ("Terms"). If you do not agree, do not use the App.</p>

    <h2>2. Subscriptions (Auto-renewable)</h2>
    <p>Onyx AI provides the following auto-renewable subscription options:</p>
    <ul>
      <li><strong>Subscription Title:</strong> Weekly Premium</li>
      <li><strong>Length of Subscription:</strong> 1 week</li>
      <li><strong>Price:</strong> $3.99 USD / week</li>
    </ul>
    <ul>
      <li><strong>Subscription Title:</strong> Monthly Premium</li>
      <li><strong>Length of Subscription:</strong> 1 month</li>
      <li><strong>Price:</strong> $17.99 USD / month</li>
    </ul>

    <p>
      <strong>Payment:</strong> Payment is charged to your Apple ID account at confirmation of purchase.
      Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current period.
      Your account may be charged for renewal within 24 hours prior to the end of the current period.
    </p>

    <p>
      <strong>Manage &amp; Cancel:</strong> You can manage or cancel subscriptions in your App Store account
      settings after purchase. If you cancel, the subscription remains active until the end of the paid period. No
      refunds are provided for unused portions through the App; refund requests are handled via Apple (Report a
      Problem / Apple Support).
    </p>

    <h2>3. Product Names</h2>
    <p>
      The subscription titles listed here must match the in-app purchase product names configured in App Store
      Connect. Make sure the exact product names shown in the App match these titles.
    </p>

    <h2>4. Acceptable Use</h2>
    <p>
      You agree not to use the App to create unlawful, offensive, defamatory, or infringing content. You are
      responsible for any content you generate or upload.
    </p>

    <h2>5. Privacy</h2>
    <p>Your use is governed by our Privacy Policy: <a className="l" href="https://www.onyxup.net/privacy">https://www.onyxup.net/privacy</a>.</p>

    <h2>6. Termination</h2>
    <p>We may suspend or terminate your access to the App for violations of these Terms.</p>

    <h2>7. Contact</h2>
    <p>For questions about these Terms, contact: <a className="l" href="mailto:onyxai.hub@gmail.com">onyxai.hub@gmail.com</a>.</p>

    <p>Terms of Use (example URL): <a className="l" href="https://www.onyxup.net/privacy">https://www.onyxup.net/privacy</a></p>
  </>
);

const privacyContentTr = (
  <>
    <h2>Gizlilik Politikası</h2>
    <p><strong>Son Güncelleme:</strong> {updated}</p>

    <h2>1. Giriş ve Kapsam</h2>
    <p>
      Onyx AI ("biz", "bizim") gizliliğinize değer verir ve kişisel bilgilerinizi korumaya kararlıdır. Bu Gizlilik
      Politikası, Onyx AI uygulamasını ("Uygulama") kullandığınızda verileri nasıl topladığımızı, kullandığımızı,
      sakladığımızı ve sildiğimizi açıklar.
    </p>

    <h2>2. Kullanıcı Uygunluğu</h2>
    <p>Bu Uygulama <strong>18 yaş ve üzeri</strong> kullanıcılar için tasarlanmıştır. Onyx AI'ı kullanarak bu yaş şartını karşıladığınızı onaylarsınız.</p>

    <h2>3. Topladığımız Bilgiler</h2>
    <ul>
      <li>
        <strong>Kamera ve Görsel Verileri:</strong> Yapay zeka destekli içerik oluşturmak için fotoğraf çekmek
        amacıyla cihazınızın kamerasına erişim talep ederiz. Bu görseller yapay zeka içerik üretimi için geçici
        olarak işlenir. Yüz tanıma veya biyometrik veri toplanmaz. Görseller saklanmaz, tutulmaz veya analiz
        edilmez. İşlendikten sonra tüm görseller anında silinir.
      </li>
      <li>
        <strong>Kullanıcı İçeriği:</strong> Kaydetmeyi seçtiğiniz görseller veya üretilen çıktılar cihazınızda yerel
        olarak veya hesabınızda (bu özellik sunuluyorsa) saklanır ve kontrolünüz altında kalır. Bu kapsamın dışında
        fotoğraf veya başka bir içerik saklamayız.
      </li>
      <li>
        <strong>Teknik ve Kullanım Verileri:</strong> Performansı ve güvenliği artırmak amacıyla anonim, toplu
        teknik veriler (cihaz modeli, işletim sistemi sürümü, uygulama kullanım kalıpları) toplayabiliriz. Bunlar
        kişisel olarak tanımlanabilir bilgi içermez.
      </li>
    </ul>

    <h2>4. Üçüncü Taraf İşleme ve Saklama</h2>
    <p>
      Görselleri işlemek ve özellik sağlamak için güvenilir üçüncü taraf hizmetleri kullanabiliriz (örneğin: model
      çıkarımı için Replicate API, geçici depolama için Google Cloud). Tüm aktarımlar şifreli kanallar (HTTPS)
      kullanır. Yüklenen görseller, siz açıkça kaydetmediğiniz sürece işlendikten <strong>24 saat</strong> içinde
      sistemlerimizden ve üçüncü taraf işlemcilerden silinir.
    </p>

    <h2>5. Bilgilerinizi Nasıl Kullanıyoruz</h2>
    <p>
      Topladığımız bilgileri Uygulama özelliklerini sağlamak, sürdürmek ve geliştirmek, güvenlik sağlamak ve yasal
      yükümlülüklere uymak için kullanırız. Kişisel verileri üçüncü taraflara satmaz veya kiralamayız.
    </p>

    <h2>6. Haklarınız</h2>
    <p>
      Bizimle iletişime geçerek (aşağıdaki iletişim bilgilerine bakın) her zaman verilerinizin silinmesini talep
      edebilirsiniz. Silme taleplerini makul bir süre içinde yanıtlayacak ve işleyeceğiz; talebi karşılamak için
      kimlik doğrulaması isteyebiliriz.
    </p>

    <h2>7. Güvenlik</h2>
    <p>
      Verileri korumak için makul teknik ve organizasyonel tedbirler uygularız. Ancak hiçbir sistem tamamen güvenli
      değildir; kesin güvenliği garanti edemeyiz.
    </p>

    <h2>8. Bu Politikadaki Değişiklikler</h2>
    <p>Bu Politikayı zaman zaman güncelleyebiliriz; önemli değişiklikler bu sayfada güncellenmiş bir "Son Güncelleme" tarihiyle yayınlanacaktır.</p>

    <h2>9. İletişim</h2>
    <p>Sorularınız, veri silme talepleriniz veya diğer talepleriniz için bizimle <a className="l" href="mailto:onyxai.hub@gmail.com">onyxai.hub@gmail.com</a> adresinden iletişime geçin.</p>

    <h2>10. Yüz Verileri</h2>
    <p>
      Uygulama herhangi bir yüz verisini toplamaz veya saklamaz. Kullanıcı tarafından yüklenen görseller yapay zeka
      destekli içerik üretmek için geçici olarak işlenir ve işlendikten sonra anında silinir. Yüz tanıma veya
      biyometrik veri saklanmaz veya paylaşılmaz.
    </p>

    <h2>11. Gizlilik Politikası URL'si</h2>
    <p>Gizlilik Politikası URL'si (örnek): <a className="l" href="https://www.onyxup.net/terms">https://www.onyxup.net/terms</a></p>
  </>
);

const termsContentTr = (
  <>
    <h2>Kullanım Şartları (EULA)</h2>
    <p><strong>Son Güncelleme:</strong> {updated}</p>

    <h2>1. Sözleşme</h2>
    <p>Onyx AI'ı ("Uygulama") indirerek veya kullanarak bu Kullanım Şartları'nı ("Şartlar") kabul edersiniz. Kabul etmiyorsanız Uygulamayı kullanmayın.</p>

    <h2>2. Abonelikler (Otomatik Yenilenen)</h2>
    <p>Onyx AI aşağıdaki otomatik yenilenen abonelik seçeneklerini sunar:</p>
    <ul>
      <li><strong>Abonelik Başlığı:</strong> Haftalık Premium</li>
      <li><strong>Abonelik Süresi:</strong> 1 hafta</li>
      <li><strong>Fiyat:</strong> Haftada 3,99 USD</li>
    </ul>
    <ul>
      <li><strong>Abonelik Başlığı:</strong> Aylık Premium</li>
      <li><strong>Abonelik Süresi:</strong> 1 ay</li>
      <li><strong>Fiyat:</strong> Ayda 17,99 USD</li>
    </ul>

    <p>
      <strong>Ödeme:</strong> Ödeme, satın alma onaylandığında Apple ID hesabınızdan tahsil edilir. Abonelikler,
      geçerli dönemin sonundan en az 24 saat önce iptal edilmediği takdirde otomatik olarak yenilenir. Hesabınız,
      geçerli dönemin sonundan 24 saat öncesine kadar yenileme için ücretlendirilebilir.
    </p>

    <p>
      <strong>Yönetme ve İptal:</strong> Satın alma sonrası App Store hesap ayarlarınızdan abonelikleri
      yönetebilir veya iptal edebilirsiniz. İptal ederseniz, abonelik ödenen dönemin sonuna kadar etkin kalır.
      Uygulama üzerinden kullanılmayan kısımlar için iade yapılmaz; iade talepleri Apple üzerinden işlenir (Sorun
      Bildir / Apple Destek).
    </p>

    <h2>3. Ürün Adları</h2>
    <p>
      Burada listelenen abonelik başlıkları, App Store Connect'te yapılandırılan uygulama içi satın alma ürün
      adlarıyla eşleşmelidir. Uygulamada görünen tam ürün adlarının bu başlıklarla eşleştiğinden emin olun.
    </p>

    <h2>4. Kabul Edilebilir Kullanım</h2>
    <p>
      Uygulamayı yasa dışı, saldırgan, iftira niteliğinde veya hak ihlali oluşturan içerik üretmek için kullanmamayı
      kabul edersiniz. Oluşturduğunuz veya yüklediğiniz her içerikten siz sorumlusunuz.
    </p>

    <h2>5. Gizlilik</h2>
    <p>Kullanımınız Gizlilik Politikamıza tabidir: <a className="l" href="https://www.onyxup.net/privacy">https://www.onyxup.net/privacy</a>.</p>

    <h2>6. Fesih</h2>
    <p>Bu Şartları ihlal etmeniz durumunda Uygulamaya erişiminizi askıya alabilir veya sonlandırabiliriz.</p>

    <h2>7. İletişim</h2>
    <p>Bu Şartlar hakkında sorularınız için iletişim: <a className="l" href="mailto:onyxai.hub@gmail.com">onyxai.hub@gmail.com</a>.</p>

    <p>Kullanım Şartları (örnek URL): <a className="l" href="https://www.onyxup.net/privacy">https://www.onyxup.net/privacy</a></p>
  </>
);

export const en = { privacyContent: privacyContentEn, termsContent: termsContentEn };
export const tr = { privacyContent: privacyContentTr, termsContent: termsContentTr };
